import { InlineFeedback } from '../../feedback/index.js';
import { useFeedback } from '../../feedback/index.js';
import { useEffect, useRef, useState } from 'react';
import { generateJSON } from '@tiptap/core';
import Modal from '../Modal.jsx';
import { CONTENT_EDITOR_HOST_LOAD_META } from './useContentEditorKernel.js';

export function ContentEditorSourceControl({ editor, getDocument, replaceDocument, recovery, openRequest = 0, hideTrigger = false }) {
  const toast = useFeedback();
  const [open, setOpen] = useState(false);
  const [format, setFormat] = useState('json');
  const [draft, setDraft] = useState('');
  const [message, setMessage] = useState('');
  const handledOpenRequest = useRef(0);
  const snapshot = useRef(null);
  const request = useRef(0);
  const close = () => { request.current += 1; setOpen(false); };
  useEffect(() => {
    if (!editor) return;
    const onLoad = ({ transaction }) => {
      if (!transaction.getMeta(CONTENT_EDITOR_HOST_LOAD_META)) return;
      request.current += 1;
      snapshot.current = null;
      setOpen(false); setDraft(''); setMessage('');
    };
    editor.on('transaction', onLoad);
    return () => editor.off('transaction', onLoad);
  }, [editor]);
  const load = (nextFormat) => {
    request.current += 1;
    snapshot.current = JSON.stringify(getDocument());
    setFormat(nextFormat);
    setDraft(nextFormat === 'json' ? JSON.stringify(getDocument(), null, 2) : editor.getHTML());
    setMessage('');
  };
  useEffect(() => {
    if (!editor || !openRequest || handledOpenRequest.current === openRequest) return;
    handledOpenRequest.current = openRequest;
    load('json'); setOpen(true);
  }, [openRequest, editor]);
  return <>
    {!hideTrigger ? <button type="button" disabled={!editor} onClick={() => { load('json'); setOpen(true); }}>Document source</button> : null}
    <Modal open={open} onOpenChange={(next) => { if (!next) close(); }} size="3xl">
      <Modal.Header onClose={close}><Modal.Title>Document source</Modal.Title><Modal.Description>Copy the current document or replace it with validated content.</Modal.Description></Modal.Header>
      <Modal.Body>
        <label>Source format<select value={format} onChange={(event) => load(event.target.value)}><option value="json">JSON</option>{!recovery ? <option value="html">HTML</option> : null}</select></label>
        <label>Document {format.toUpperCase()}<textarea className="w-full min-h-64 font-mono" value={draft} aria-describedby={message ? "document-source-error" : undefined} aria-invalid={Boolean(message)} onChange={(event) => { request.current += 1; setDraft(event.target.value); setMessage(''); }}/></label>
        {message ? <InlineFeedback id="document-source-error" focus>{message}</InlineFeedback> : null}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" onClick={async () => { try { await navigator.clipboard.writeText(draft); toast.success('Source copied.', { id: 'action:ContentEditorSourceControl:action' }); } catch { toast.error('Clipboard access is unavailable.', { id: 'action:ContentEditorSourceControl:action' }); } }}>Copy source</button>
        <button type="button" disabled={!editor?.isEditable || Boolean(recovery)} onClick={async () => {
          const token = ++request.current;
          try { const text = await navigator.clipboard.readText(); if (token === request.current) { setDraft(text); setMessage(''); } }
          catch { if (token === request.current) toast.error('Clipboard access is unavailable.', { id: 'action:ContentEditorSourceControl:action' }); }
        }}>Paste source</button>
        <button type="button" disabled={!editor?.isEditable || Boolean(recovery)} onClick={() => {
          if (JSON.stringify(getDocument()) !== snapshot.current) { setMessage('The document changed while source was open. Close and reopen it before applying changes.'); return; }
          try {
            const candidate = format === 'json' ? JSON.parse(draft) : generateJSON(draft, editor.extensionManager.extensions);
            if (replaceDocument(candidate)) { toast.success('Document source applied.', { id: 'document-source-apply' }); close(); }
            else setMessage('Source could not be applied. Check the document format, profile requirements, and protected blocks.');
          } catch (error) { setMessage(error.message || 'Source is invalid.'); }
        }}>Apply source</button>
        <button type="button" onClick={close}>Close source</button>
      </Modal.Footer>
    </Modal>
  </>;
}
